import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Select, Spin } from 'antd';
import WarrantyModal from './WarrantyModal';
import { getFormPopupContainer } from 'components/shared/BWFormControl/popupHelpers';
import { previewWarrantyTypeDelete, deleteWarrantyType } from 'services/warranty-management.service';
import { getErrorMessage } from 'utils/index';
import { showToast } from 'utils/helpers';

export default function WarrantyTypeDeleteForm({ item, companyId, onClose, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [targetId, setTargetId] = useState();
  const [stepCode, setStepCode] = useState();
  const [page, setPage] = useState(1);
  const sequence = useRef(0);
  const target = data?.targets.find(row => row.warranty_type_id === targetId);
  const valid = data && !loading && !busy && !data.restricted_count && (!data.total || (target && (!data.pending_count || target.steps.some(step => step.code === stepCode))));
  const load = useCallback(async () => {
    const current = ++sequence.current;
    setLoading(true); setError(''); setData(null); setTargetId(undefined); setStepCode(undefined); setPage(1);
    try {
      const result = await previewWarrantyTypeDelete(item.warranty_type_id, { company_id: companyId });
      if (current === sequence.current) setData(result);
    } catch (e) { if (current === sequence.current) setError(getErrorMessage(e)); }
    finally { if (current === sequence.current) setLoading(false); }
  }, [item.warranty_type_id, companyId]);
  useEffect(() => { load(); return () => { sequence.current += 1; }; }, [load]);
  const submit = async () => {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      const result = await deleteWarrantyType(item.warranty_type_id, { company_id: companyId,
        expected_version: data.source.version_no, preview_token: data.preview_token,
        target_type_id: targetId, target_token: target?.target_token, target_step_code: stepCode });
      showToast.success(result.transferred_count ? `Đã chuyển ${result.transferred_count} phiếu và xóa hình thức bảo hành.` : 'Đã xóa hình thức bảo hành.');
      await onDeleted();
    } catch (e) { setError(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  return <WarrantyModal open size='lg' title='Xóa hình thức bảo hành' description={`${item.type_code} — ${item.type_name}`}
    onClose={onClose} loading={busy} closeDisabled={busy} maskClosable={false} cancelAction={{ label: 'Hủy' }}
    primaryAction={{ label: data?.total ? `Chuyển ${data.total} phiếu và xóa` : 'Xóa hình thức', type: 'danger', icon: 'fi fi-rr-trash', disabled: !valid, loading: busy, onClick: submit }}>
    <div className='bw_warranty_type_delete'>
      {loading && <Spin tip='Đang kiểm tra các phiếu của hình thức này...' />}
      {error && <Alert type='error' showIcon message={error} action={<button type='button' disabled={busy || loading} onClick={load}>Tải lại</button>} />}
      {data && <>
        <Alert type={data.total ? 'warning' : 'info'} showIcon message={data.total
          ? `Có ${data.total} phiếu bảo hành thuộc hình thức này trong công ty.`
          : 'Hình thức này chưa có phiếu bảo hành. Anh có thể xác nhận xóa.'}
          description={data.total ? `${data.pending_count} phiếu đang xử lý · ${data.finished_count} phiếu đã kết thúc.` : undefined} />
        {Boolean(data.restricted_count) && <Alert type='error' showIcon message={`${data.restricted_count} phiếu ngoài cửa hàng được phân công. Người được phân công đầy đủ cần thực hiện chuyển loại và xóa.`} />}
        {Boolean(data.total) && <>
          <div className='bw_warranty_type_delete_field'>
            <label htmlFor='warranty-delete-target'>Chuyển các phiếu sang hình thức <span aria-hidden='true'>*</span></label>
            <Select id='warranty-delete-target' aria-label='Hình thức chuyển sang' showSearch optionFilterProp='label'
              placeholder='Chọn hình thức nhận các phiếu' value={targetId} disabled={busy} getPopupContainer={getFormPopupContainer}
              onChange={value => { setTargetId(value); setStepCode(undefined); setError(''); }}
              options={data.targets.map(row => ({ value: row.warranty_type_id, label: `${row.type_code} — ${row.type_name}` }))} />
          </div>
          {!data.targets.length && <Alert type='info' showIcon message='Chưa có hình thức khác đang sử dụng. Khai báo hình thức nhận các phiếu trước khi xóa.' />}
          {Boolean(data.pending_count) && <div className='bw_warranty_type_delete_field'>
            <label htmlFor='warranty-delete-step'>Phiếu đang xử lý tiếp tục từ bước <span aria-hidden='true'>*</span></label>
            <Select id='warranty-delete-step' aria-label='Bước tiếp tục' placeholder={target ? 'Chọn bước tiếp tục' : 'Chọn hình thức trước'}
              value={stepCode} onChange={setStepCode} disabled={busy || !target} getPopupContainer={getFormPopupContainer}
              options={(target?.steps || []).map((step, index) => ({ value: step.code, label: `${index + 1}. ${step.name}` }))} />
            {target && !target.steps.length && <p role='alert'>Hình thức này chưa có bước xử lý. Bổ sung bước trước khi chuyển các phiếu đang xử lý.</p>}
            <p>Các bước trước bước đã chọn sẽ được bỏ qua. Nghiệp vụ đã thực hiện vẫn được ghi nhận; nếu bước đã chọn đã xong thì phiếu tiếp tục tới bước kế tiếp.</p>
          </div>}
          <p>Phiếu đã kết thúc chuyển sang loại mới, giữ trạng thái và quy trình lịch sử. Báo giá, thu chi, hóa đơn và chứng từ kho của các phiếu giữ nguyên.</p>
          <details className='bw_warranty_type_delete_requests'><summary>Xem {data.total} phiếu bị ảnh hưởng</summary>
            <div className='bw_warranty_type_delete_table'><table><thead><tr><th>Mã bảo hành</th><th>Cửa hàng đang xử lý</th><th>Trạng thái</th></tr></thead>
              <tbody>{data.requests.slice((page - 1) * 20, page * 20).map(row => <tr key={row.warranty_request_id}>
                <td>{row.request_code}</td><td>{row.store_name || '—'}</td><td>{row.status_name}</td></tr>)}</tbody></table></div>
            {data.total > 20 && <div className='bw_warranty_type_delete_paging'>
              <button type='button' disabled={page === 1} onClick={() => setPage(value => value - 1)}>Trang trước</button>
              <span>Trang {page} / {Math.ceil(data.total / 20)}</span>
              <button type='button' disabled={page * 20 >= data.total} onClick={() => setPage(value => value + 1)}>Trang sau</button>
            </div>}
          </details>
        </>}
      </>}
    </div>
  </WarrantyModal>;
}
